import { RightOutlined } from "@ant-design/icons";
import { Button, Spin, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useView } from "../contexts/ViewContext";
import {
  Marker,
  NotInPlanMarker,
  Spine,
  SubtopicList,
  SubtopicRow,
  TopicGroup,
  TopicHeader,
  TopicName,
  spineStyles,
} from "../components/spine/Spine";

import styles from "./Plan.module.css";

const { Title, Text } = Typography;

const STATUS_LABELS = {
  current: "You are here",
  ahead: "Ahead",
};

const topicStatus = (topic) => {
  const statuses = topic.subtopics.map((subtopic) => subtopic.status);
  if (statuses.every((status) => status === "done")) return "done";
  if (statuses.some((status) => status === "current")) return "current";
  return "ahead";
};

const PlanSubtopicRow = ({
  subtopic,
  status,
  currentStepProgress,
  currentRowRef,
  learningJourneyId,
  hideChip,
}) => {
  const isCurrent = status === "current";
  const navigate = useNavigate();

  const name = subtopic.entry_step_id ? (
    <Text
      className={`${spineStyles.subtopicName} ${styles.subtopicNameLink}`}
      onClick={() =>
        navigate(`/t/${learningJourneyId}/step/${subtopic.entry_step_id}`)
      }
    >
      {subtopic.name}{" "}
      <RightOutlined className={styles.subtopicNameChevron} />
    </Text>
  ) : (
    <Text
      className={`${spineStyles.subtopicName} ${isCurrent ? styles.subtopicNameCurrent : ""}`}
    >
      {subtopic.name}
    </Text>
  );

  const trailing = (
    <>
      {/* Only when a dot actually has something to point at: standing on the
          subtopic's close, every sibling step is "completed" and none is
          "current" (the close itself has no dot -- see LearningStep.progress()),
          so the strip would otherwise render a fully-done row that contradicts
          the "current" marker sitting right next to it. */}
      {isCurrent &&
        currentStepProgress?.steps.some((step) => step.state === "current") && (
          <div className={styles.stepStrip}>
            {currentStepProgress.steps.map((step, i) => (
              <span
                key={i}
                className={`${styles.stepDot} ${styles[`step-${step.state}`]}`}
              />
            ))}
          </div>
        )}
      {subtopic.unsolved_step_ids.length > 0 && (
        <div className={styles.unsolvedRings}>
          {subtopic.unsolved_step_ids.map((stepId) => (
            <span
              key={stepId}
              className={styles.unsolvedRing}
              title="Never solved"
              onClick={() => navigate(`/t/${learningJourneyId}/step/${stepId}`)}
            />
          ))}
        </div>
      )}
      {!subtopic.included && !hideChip && (
        <span className={styles.notInPlanChip}>Not in plan</span>
      )}
    </>
  );

  return (
    <SubtopicRow
      rowRef={isCurrent ? currentRowRef : null}
      className={`${styles.subtopicRow} ${isCurrent ? styles.subtopicRowCurrent : ""}`}
      leading={
        subtopic.included ? (
          <Marker className={styles[`marker-${status}`]} />
        ) : (
          <NotInPlanMarker className={styles.notInPlanMarkerBox} />
        )
      }
      trailing={trailing}
    >
      {name}
    </SubtopicRow>
  );
};

const PlanTopicGroup = ({
  topic,
  number,
  currentStepProgress,
  currentRowRef,
  learningJourneyId,
}) => {
  const status = topicStatus(topic);
  const totalSubtopics = topic.subtopics.length;
  const completedSubtopics = topic.subtopics.filter(
    (subtopic) => subtopic.status === "done",
  ).length;

  return (
    <TopicGroup>
      <TopicHeader>
        <span className={styles.topicNumber}>
          {topic.included ? String(number).padStart(2, "0") : "–"}
        </span>
        <TopicName>{topic.name}</TopicName>
        {topic.included ? (
          <span
            className={`${styles.topicState} ${styles[`topicState-${status}`]}`}
          >
            {status === "done"
              ? `${completedSubtopics} of ${totalSubtopics}`
              : STATUS_LABELS[status]}
          </span>
        ) : (
          <span className={styles.notInPlanChip}>Not in plan</span>
        )}
      </TopicHeader>
      <SubtopicList>
        {topic.subtopics.map((subtopic, index) => (
          <PlanSubtopicRow
            key={index}
            subtopic={subtopic}
            status={subtopic.status}
            currentStepProgress={currentStepProgress}
            currentRowRef={currentRowRef}
            learningJourneyId={learningJourneyId}
            hideChip={!topic.included}
          />
        ))}
      </SubtopicList>
    </TopicGroup>
  );
};

const RecalibrationBand = ({
  onAdjust,
  onDismiss,
  dismissing,
  adjusting,
  closing,
}) => {
  return (
    <div
      className={`${styles.recalibrationBandWrap} ${closing ? styles.closing : ""}`}
    >
      <div className={styles.recalibrationBand}>
        <Text className={styles.recalibrationBandText}>
          Still the right plan?
        </Text>
        <div className={styles.recalibrationBandActions}>
          <Button
            type="text"
            className={styles.recalibrationDismiss}
            onClick={onDismiss}
            disabled={adjusting || dismissing}
          >
            Not now
          </Button>
          <Button
            type="text"
            className={styles.recalibrationAdjust}
            onClick={onAdjust}
            loading={adjusting}
            disabled={dismissing}
          >
            Adjust
          </Button>
        </div>
      </div>
    </div>
  );
};

const Plan = () => {
  const { view, learningJourneyId, readView, setView } = useView();
  const currentRowRef = useRef(null);
  const navigate = useNavigate();
  const [startingRecalibration, setStartingRecalibration] = useState(false);
  const [bandBusy, setBandBusy] = useState(false);
  const [bandClosing, setBandClosing] = useState(false);

  useEffect(() => {
    currentRowRef.current?.scrollIntoView({ block: "center" });
  }, []);

  const startRecalibration = async () => {
    setStartingRecalibration(true);
    try {
      await api.post("/workspace/recalibration/start/", {
        learning_journey_id: learningJourneyId,
      });
      setView(await readView());
      navigate(`/t/${learningJourneyId}`);
    } catch (err) {
      setStartingRecalibration(false);
      throw err;
    }
  };

  const dismissRecalibrationBand = async () => {
    setBandBusy(true);
    setBandClosing(true);
    try {
      await api.post("/workspace/roadmap/dismiss-recalibration-prompt/", {
        learning_journey_id: learningJourneyId,
      });
      setView(await readView());
    } finally {
      setBandBusy(false);
    }
  };

  if (view === null) {
    return (
      <div className={styles.centered}>
        <Spin size="large" />
      </div>
    );
  }

  if (!view.plan) {
    return <Navigate to={`/t/${learningJourneyId}`} replace />;
  }

  // Numbering counts only on-route topics, so a later live topic takes the
  // number a dropped one would have had (dropped topics still render, with a
  // dash, to keep the trace of what the user did in them).
  let liveTopicIndex = 0;

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <Title level={2} className={styles.pageTitle}>
          Your plan
        </Title>
        {view.plan.offer_recalibration && (
          <RecalibrationBand
            onAdjust={startRecalibration}
            onDismiss={dismissRecalibrationBand}
            adjusting={startingRecalibration}
            dismissing={bandBusy}
            closing={bandClosing}
          />
        )}
        <Spine>
          {view.plan.topics.map((topic, index) => {
            const number = topic.included ? ++liveTopicIndex : null;
            return (
              <PlanTopicGroup
                key={index}
                topic={topic}
                number={number}
                currentStepProgress={view.progress}
                currentRowRef={currentRowRef}
                learningJourneyId={learningJourneyId}
              />
            );
          })}
        </Spine>
        {view.plan.recalibration_available && (
          <div className={styles.recalibrationFooter}>
            <Text className={styles.recalibrationFooterText}>
              Not learning what you need?
            </Text>
            <Button
              type="text"
              className={styles.recalibrationFooterLink}
              onClick={startRecalibration}
              loading={startingRecalibration}
            >
              Adjust plan
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Plan;
